import React, { useEffect, useState } from 'react';
import {
  getOptionLabel,
  getButtonSpinner,
  getDataGridHeading,
} from 'utils/commonUtils';
import * as yup from 'yup';
import {
  CheckedCoursesColumns,
  getCheckedCoursesListData,
} from './courseChecksListData';
import MDBox from 'components/MDBox';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { GridWrapper } from 'components/Ui/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import { intialPageSize, requiredField } from 'constants';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { getSearchedStudents } from 'layouts/students/service/studentsApi';
import { useNotification, pageSizeChangeHandler } from 'utils/commonUtils';
import { setIsChanged } from 'layouts/applications/attendance/store/semesterSlice';
import { getWeeksData } from 'layouts/applications/attendance/services/attendanceApis';
import { setSelectSemester } from 'layouts/applications/attendance/store/semesterSlice';
import { FormInputAutoCompleteSearch } from 'components/Common/Forms/FormAutoCompleteSearch';
import { getAttendanceFilterOptions } from 'layouts/applications/attendance/services/attendanceApis';
import { getCourseChecks } from 'layouts/studentSuccessAdvocate/FilterPresets/services/courseChecksApi';

const schema = yup.object().shape({
  student_id: yup.string().required(requiredField),
  selectedWeek: yup.string().required(requiredField),
});

const CourseChecksComponent = () => {
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const semesterList = useSelector((state) => state?.semesterList);
  const { selectedSemester } = semesterList || {};

  const dispatch = useDispatch();

  const [student, setStudent] = useState(null);
  const [weeksData, setWeeksData] = useState(null);
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [semesterDropDownList, setsemesterDropDownList] = useState();
  const [isStudentSearchLoading, setIsStudentSearchLoading] = useState(false);

  const [courseChecksListData, setCourseChecksListData] = useState({
    isLoading: false,
    rows: [],
    pageSize: intialPageSize,
  });

  const defaultValue = {
    student_id: '',
    selectedWeek: '',
    selectedSemester: selectedSemester,
  };

  const useFunction = useForm({
    defaultValues: defaultValue,
    resolver: yupResolver(schema),
  });

  const { control, setValue, register, formState, clearErrors, handleSubmit } =
    useFunction;

  const { errors } = formState;

  useEffect(() => {
    if (semesterDropDownList?.length > 0) {
      const firstSemester = semesterDropDownList[0];
      dispatch(setSelectSemester(firstSemester));
      setValue('selectedSemester', firstSemester);
    }
  }, [semesterDropDownList]);

  useEffect(() => {
    fetchAttendanceFilterOptions();
  }, []);

  useEffect(() => {
    fetchWeeksOptions();
  }, [selectedSemester]);

  useEffect(() => {
    if (studentSearchQuery?.length >= 1) fetchSearchedStudents();
  }, [studentSearchQuery]);

  const fetchSearchedStudents = async () => {
    try {
      setIsStudentSearchLoading(true);
      const searchedStudents = await getSearchedStudents(
        studentSearchQuery,
        true,
        false,
        20
      );
      setStudentOptions(searchedStudents?.items || []);
      setIsStudentSearchLoading(false);
    } catch (error) {
      setIsStudentSearchLoading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const fetchAttendanceFilterOptions = async () => {
    try {
      const attendanceFilterOptionsData = await getAttendanceFilterOptions();
      setsemesterDropDownList(attendanceFilterOptionsData?.semesters);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const fetchWeeksOptions = async () => {
    const weeksOptionsData = await getWeeksData(selectedSemester, true);
    setWeeksData(weeksOptionsData);
  };

  const weekDropDownList = weeksData?.map((item) => ({
    id: item?.descr,
    value: item?.descr,
  }));

  const semesterDropDownLists = semesterDropDownList?.map((item, index) => ({
    id: item,
    value: item,
  }));

  const handleSemesterChange = (e) => {
    const newSemester = e?.target?.value;

    dispatch(setIsChanged(true));
    dispatch(setSelectSemester(newSemester));
    setValue('selectedSemester', newSemester);

    setWeeksData([]);
    setValue('selectedWeek', '');
  };

  const handleGetCourseChecks = async (data) => {
    try {
      const { student_id, selectedWeek, selectedSemester } = data;
      setCourseChecksListData((prev) => ({ ...prev, isLoading: true }));
      const checkedCoursesData = await getCourseChecks(
        student_id,
        selectedWeek,
        selectedSemester
      );

      setCourseChecksListData((prev) => ({
        ...prev,
        isLoading: false,
        rows: getCheckedCoursesListData(checkedCoursesData, student_id),
      }));
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
      setCourseChecksListData((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleDelete = (params) => {
    console.log('params', params.row);
  };

  const handleClearFilters = () => {
    setValue('selectedWeek', '');
    setValue('student_id', '');

    clearErrors(['student_id', 'selectedWeek']);

    setStudent(null);
    setStudentOptions([]);
    setStudentSearchQuery('');

    if (semesterDropDownList && semesterDropDownList?.length > 0) {
      const firstSemester = semesterDropDownList[0];
      dispatch(setSelectSemester(firstSemester));
      setValue('selectedSemester', firstSemester);
    }

    setCourseChecksListData((prev) => ({
      ...prev,
      rows: [],
    }));
  };

  const handleStudentSelect = (val) => {
    const studentId = val?.studentId || '';
    setStudent(val);
    setValue('student_id', studentId, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const pageSizeChange = pageSizeChangeHandler(setCourseChecksListData);

  return (
    <>
      <MDBox>
        <Card>
          {getDataGridHeading('Course Checks', 'New Course Check')}
          <GridWrapper container spacing={2}>
            <Grid item xs={8}>
              <FormInputAutoCompleteSearch
                value={student}
                label="Search students"
                options={studentOptions}
                error={!!errors?.student_id}
                placeholder="Search students"
                getOptionLabel={getOptionLabel}
                setSelectedInput={handleStudentSelect}
                helperText={errors?.student_id?.message}
                isSearchloading={isStudentSearchLoading}
                setInputSearchQuery={setStudentSearchQuery}
              />
            </Grid>

            <Grid item xs={4}>
              {getButtonSpinner(false, handleClearFilters, 'Clear Filters')}
            </Grid>

            <Grid item xs={4}>
              <FormInputDropdown
                control={control}
                name="selectedSemester"
                label="Select Semester"
                options={semesterDropDownLists}
                register={register('selectedSemester', {
                  onChange: (e) => {
                    handleSemesterChange(e);
                  },
                })}
              />
            </Grid>

            <Grid item xs={4}>
              <FormInputDropdown
                control={control}
                name="selectedWeek"
                label="Select Week"
                options={weekDropDownList}
                error={!!errors?.selectedWeek}
                register={register('selectedWeek')}
                helperText={errors?.selectedWeek?.message}
              />
            </Grid>

            <Grid item xs={4}>
              {getButtonSpinner(
                false,
                handleSubmit(handleGetCourseChecks),
                'Get Course Checks'
              )}
            </Grid>
          </GridWrapper>
          <DeltaDataGrid
            setListData={pageSizeChange}
            listData={courseChecksListData}
            columns={CheckedCoursesColumns(handleDelete)}
          />
        </Card>
      </MDBox>
      <NotificationPopup />
    </>
  );
};

export default CourseChecksComponent;
